// Persistent "current organization" context. The app is multi-org; this module
// is the single source of truth for which org the user is currently working in,
// mirrored to localStorage so it survives reloads. URL/route context always wins
// over the persisted value (see resolveFromRoute), so a shared deep link never
// lands you in the wrong workspace.

const STORAGE_KEY = 'continuum.currentOrgId'

const pickDefaultOrg = (orgs) => {
  if (!orgs || orgs.length === 0) {
    return null
  }

  const sorted = [...orgs].sort((a, b) => {
    const at = a.updatedAt || a.createdAt || ''
    const bt = b.updatedAt || b.createdAt || ''

    return String(bt).localeCompare(String(at))
  })

  return sorted[0]?.id || orgs[0].id
}

export const currentOrg = {
  namespaced: true,
  state: {
    currentOrgId: null,
    // false until hydrate() has validated the persisted value against the user's
    // actual org list — the switcher stays hidden until then to avoid flicker.
    hydrated: false
  },
  getters: {
    currentOrgId: (state) => state.currentOrgId,
    // Operational org list (switcher, current-org resolution, reconcile, workspace
    // lists): excludes ARCHIVED (soft-deleted) orgs for everyone — including
    // SuperAdmins. You don't switch into or work in an archived org; SuperAdmins
    // manage/restore them from the Admin → Organizations "Archived" tab, which reads
    // the raw organisations state directly (not this getter).
    orgs: (state, getters, rootState) => (rootState.organisations.organisations || []).filter((o) => !o.deletedAt),
    currentOrg: (state, getters) => getters.orgs.find((o) => o.id === state.currentOrgId) || null,
    currentOrgRole: (state, getters, rootState, rootGetters) =>
      rootGetters['orgMembers/getCallerRoleInOrg'](state.currentOrgId),
    hasOrgs: (state, getters) => getters.orgs.length > 0,
    isSingleOrg: (state, getters) => getters.orgs.length === 1
  },
  mutations: {
    setCurrentOrgId (state, orgId) {
      state.currentOrgId = orgId || null
      if (orgId) {
        localStorage.setItem(STORAGE_KEY, orgId)
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    },
    setHydrated (state, value) {
      state.hydrated = value
    }
  },
  actions: {
    // Run once after login (and whenever the org list is (re)loaded). Resolves the
    // current org from: single org -> that org; else persisted-if-valid; else the
    // most-recently-updated org.
    async hydrate ({ commit, dispatch, getters, rootState }) {
      if (!rootState.organisations.organisations) {
        try {
          await dispatch('organisations/listOrganisations', null, { root: true })
        } catch (e) {
          console.error(e)
        }
      }

      const orgs = getters.orgs

      if (orgs.length === 0) {
        commit('setCurrentOrgId', null)
        commit('setHydrated', true)

        return
      }

      let orgId
      if (orgs.length === 1) {
        orgId = orgs[0].id
      } else {
        const persisted = localStorage.getItem(STORAGE_KEY)
        orgId = persisted && orgs.some((o) => o.id === persisted) ? persisted : pickDefaultOrg(orgs)
      }
      commit('setCurrentOrgId', orgId)
      commit('setHydrated', true)

      // Load the org's membership so canWriteInOrg/getCallerRoleInOrg (orgMembers.js) resolve
      // correctly on the very first page a user lands on. Without this, membersByOrg[orgId]
      // stays empty for anyone who isn't the org creator or SuperAdmin until they happen to
      // visit an org-owner-gated route, silently forcing every ordinary OWNER/MEMBER of an
      // admin-provisioned org into read-only "View only mode" on normal navigation.
      if (orgId) {
        dispatch('orgMembers/listOrgMembers', { orgId }, { root: true }).catch(() => {})
      }
    },

    // Explicit switch from the org switcher. Loads the org's members so role/admin
    // resolves, and (optionally) routes to the org-scoped home.
    setCurrentOrg ({ commit, dispatch }, { orgId, navigate = false, router = null }) {
      commit('setCurrentOrgId', orgId)
      if (orgId) {
        dispatch('orgMembers/listOrgMembers', { orgId }, { root: true }).catch(() => {})
      }
      if (navigate && router) {
        router.push('/dashboard/').catch(() => {})
      }
    },

    // URL/deep-link override: when a route carries an org the user belongs to, it
    // becomes the current org (the URL is the source of truth for that navigation).
    resolveFromRoute ({ commit, getters, state }, orgId) {
      if (!orgId || orgId === state.currentOrgId) {
        return
      }
      if (getters.orgs.some((o) => o.id === orgId)) {
        commit('setCurrentOrgId', orgId)
      }
    },

    // Recover from a stale current org (org deleted / user removed): fall back
    // through persisted-valid -> most-recent -> null.
    reconcile ({ commit, getters, state }) {
      const orgs = getters.orgs
      if (state.currentOrgId && orgs.some((o) => o.id === state.currentOrgId)) {
        return
      }

      const persisted = localStorage.getItem(STORAGE_KEY)
      if (persisted && orgs.some((o) => o.id === persisted)) {
        commit('setCurrentOrgId', persisted)
      } else {
        commit('setCurrentOrgId', pickDefaultOrg(orgs))
      }
    }
  }
}
